import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  /**
   * Push a custom event to the GTM dataLayer. GTM picks it up and (if a
   * matching Custom Event trigger is configured in the GTM UI) fires a
   * GA4 Event tag.
   *
   * The site loads GTM (GTM-5LPGMCQ) — NOT gtag.js directly — so calling
   * gtag(...) here would throw. dataLayer is GTM's official mechanism.
   *
   * In GTM:
   *   Trigger: Custom Event, Event Name = matches `eventName` argument
   *   Tag: GA4 Event with parameters event_category, event_label, value
   */
  trackEvent(eventName: string, eventDetails: string, eventCategory: string) {
    if (typeof window === 'undefined') return;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: eventName,
      event_category: eventCategory,
      event_label: eventName,
      value: eventDetails,
    });
  }

  /**
   * Fire the OpenAI/ChatGPT Ads "appointment_scheduled" conversion on a completed
   * booking. Sends it BOTH ways with one shared id so OpenAI de-duplicates:
   *   1. browser pixel  — oaiq('measure', …, { event_id })
   *   2. first-party relay — POST /api/oai-conversion → server Conversions API
   *      (more resilient to ad blockers; the server adds the secret key, see server.ts)
   * Call once per booking (from the thank-you page and the desktop popup listener).
   */
  trackBookingConversion() {
    if (typeof window === 'undefined') return;
    const w = window as any;
    const eventId =
      (w.crypto?.randomUUID?.() as string | undefined) ??
      `appt_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    w.oaiq?.('measure', 'appointment_scheduled', { type: 'customer_action' }, { event_id: eventId });

    try {
      fetch('/api/oai-conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: eventId, source_url: w.location?.href }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore — the browser pixel above still recorded the conversion */
    }

    // Meta: a completed booking is the standard `Schedule` event. Its own
    // event_id (distinct from the OpenAI appt id above) is shared between the
    // browser pixel and the CAPI relay so Meta de-duplicates the two.
    this.trackMetaEvent('Schedule');
  }

  /**
   * Fire the Meta `Lead` event on scheduling intent (Calendly open). Dual-path:
   * browser pixel + first-party CAPI relay sharing one event_id. Called from
   * CalendlyService.openPopup alongside the existing schedule_call_click event.
   */
  trackLead() {
    this.trackMetaEvent('Lead');
  }

  /**
   * Send a Meta standard event BOTH ways with one shared event_id so Meta
   * de-duplicates Pixel (browser) vs Conversions API (server):
   *   1. browser pixel    — fbq('track', name, {}, { eventID })
   *   2. first-party relay — POST /api/meta-capi → server CAPI (adds the secret
   *      access token + client IP/User-Agent; see server.ts). More resilient to
   *      iOS/ITP, ad-blockers and cookie loss.
   * fbp/fbc are read from cookies (set by the base pixel) and forwarded so the
   * server event can be matched to a person.
   */
  private trackMetaEvent(eventName: 'Lead' | 'Schedule') {
    if (typeof window === 'undefined') return;
    const w = window as any;
    const eventId =
      (w.crypto?.randomUUID?.() as string | undefined) ??
      `${eventName.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    w.fbq?.('track', eventName, {}, { eventID: eventId });

    const { fbp, fbc } = this.getFbCookies();
    try {
      fetch('/api/meta-capi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: eventName,
          event_id: eventId,
          event_source_url: w.location?.href,
          fbp,
          fbc,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore — the browser pixel above still recorded the event */
    }
  }

  /**
   * Read Meta's _fbp / _fbc cookies (set by the base pixel) for server-side
   * advanced matching. If _fbc is absent but the URL carries an `fbclid` (a
   * fresh ad click before the pixel wrote the cookie), synthesize it in Meta's
   * documented `fb.1.<ms>.<fbclid>` format so the click is still attributable.
   */
  private getFbCookies(): { fbp?: string; fbc?: string } {
    if (typeof document === 'undefined') return {};
    const read = (name: string): string | undefined => {
      const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : undefined;
    };
    const fbp = read('_fbp');
    let fbc = read('_fbc');
    if (!fbc) {
      const fbclid = new URLSearchParams(window.location?.search).get('fbclid');
      if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
    return { fbp, fbc };
  }
}
